import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import cx from 'classnames';
import { useStoreContext } from '../../../../factorys/useStoreContext';
import { IdentityContext } from '../../../App/identity.context';
import { GetClassCoursesDataDocument } from '../../../../services/graphql/types/graphql';

export interface CourseCascaderProps {
  setAllQueryParams: (params: { courseId: string; lessonId: string; assignmentId: string }) => void;
  isGroupAssignment?: boolean;
  children?: React.ReactNode;
}

export const CourseCascader: React.FC<CourseCascaderProps> = ({
  setAllQueryParams,
  isGroupAssignment,
  children,
}) => {
  const [searchParams] = useSearchParams();
  const { 
    currentClassId,
    currentClass,
    setCurrentClassId,
    availableClasses,
    
    currentSemesterId,
    currentSemester,
    setCurrentSemesterId,
    availableSemesters,
    
    currentSchoolId,
    currentSchool,
    availableSchools,
  } = useStoreContext(IdentityContext);
  
  const [isOpen, setIsOpen] = useState(false);
  
  // Temporary state for selection (only committed on confirm)
  const [tempSemester, setTempSemester] = useState<string | null>(null);
  const [tempClass, setTempClass] = useState<string | null>(null);
  const [tempCourse, setTempCourse] = useState<string | null>(null);
  const [tempLesson, setTempLesson] = useState<string | null>(null);
  const [tempAssignment, setTempAssignment] = useState<string | null>(null);
  
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get current values from URL
  const urlCourseId = searchParams.get('courseId');
  const urlLessonId = searchParams.get('lessonId');
  const urlAssignmentId = searchParams.get('assignmentId');

  // Debug: Log URL parameters
  useEffect(() => {
    console.log('[CourseCascader] URL params changed:', {
      urlCourseId,
      urlLessonId,
      urlAssignmentId,
    });
  }, [urlCourseId, urlLessonId, urlAssignmentId]);

  // Fetch course data based on currentClassId (for display) and tempClass (for preview)
  const { data, loading, refetch } = useQuery(GetClassCoursesDataDocument, {
    variables: { classId: currentClassId || '' },
    skip: !currentClassId,
  });

  // Debug: Log main query data
  useEffect(() => {
    console.log('[CourseCascader] Main query data:', {
      currentClassId,
      loading,
      coursesCount: data?.courses?.length,
      courses: data?.courses?.map(c => ({ id: c.courseId, name: c.name })),
    });
  }, [data, currentClassId, loading]);

  // Fetch preview data for temp class when selecting
  const { data: tempData, loading: tempLoading, refetch: tempRefetch } = useQuery(GetClassCoursesDataDocument, {
    variables: { classId: tempClass || '' },
    skip: !tempClass || tempClass === currentClassId,
  });

  // Debug: Log temp query data
  useEffect(() => {
    if (tempClass && tempClass !== currentClassId) {
      console.log('[CourseCascader] Temp query data:', {
        tempClass,
        tempLoading,
        coursesCount: tempData?.courses?.length,
        courses: tempData?.courses?.map(c => ({ id: c.courseId, name: c.name })),
      });
    }
  }, [tempData, tempClass, currentClassId, tempLoading]);

  // Use temp courses when selecting, otherwise use current courses
  const courses = useMemo(() => {
    if (isOpen && tempClass && tempClass !== currentClassId && tempData) {
      console.log('[CourseCascader] Using temp courses for preview:', tempData?.courses?.length);
      return tempData?.courses || [];
    }
    console.log('[CourseCascader] Using main courses for display:', data?.courses?.length);
    return data?.courses || [];
  }, [data, tempData, isOpen, tempClass, currentClassId]);

  // Get all classes from the current school (not filtered by currentSemesterId)
  const allSchoolClasses = useMemo(() => {
    return currentSchool?.classes ?? [];
  }, [currentSchool]);

  // Debug: Log current context
  useEffect(() => {
    console.log('[CourseCascader] Context changed:', {
      currentSemesterId,
      currentSemester: currentSemester?.name,
      currentClassId,
      currentClass: currentClass?.name,
      isOpen,
    });
  }, [currentSemesterId, currentSemester, currentClassId, currentClass, isOpen]);

  // Initialize temp state when opening dropdown
  const handleOpen = () => {
    console.log('[CourseCascader] Opening with:', {
      currentSemesterId,
      currentClassId,
      urlCourseId,
      urlLessonId,
      urlAssignmentId,
      availableClasses: availableClasses?.length,
      allSchoolClasses: allSchoolClasses.length,
      availableSemesters: availableSemesters?.length,
    });
    setTempSemester(currentSemesterId ?? null);
    setTempClass(currentClassId);
    setTempCourse(urlCourseId);
    setTempLesson(urlLessonId);
    setTempAssignment(urlAssignmentId);
    setIsOpen(true);
  };

  // Reset temp state and close
  const handleCancel = () => {
    setIsOpen(false);
    // Clear temp states when canceling
    setTempSemester(null);
    setTempClass(null);
    setTempCourse(null);
    setTempLesson(null);
    setTempAssignment(null);
  };

  // Refetch courses when temp class changes
  useEffect(() => {
    if (tempClass && tempClass !== currentClassId) {
      tempRefetch({ classId: tempClass });
    }
  }, [tempClass, currentClassId, tempRefetch]);

  const filteredClasses = useMemo(() => {
    if (!tempSemester || !allSchoolClasses) {
      console.log('[CourseCascader] No filteredClasses:', { 
        tempSemester, 
        allSchoolClasses: allSchoolClasses?.length 
      });
      return [];
    }
    
    // Filter classes by semester through LessonSchedule
    const classIdsInSemester = new Set(
      allSchoolClasses
        .flatMap((c) => c.lessonSchedule ?? [])
        .filter((ls) => ls.semester?.semesterId === tempSemester)
        .map((ls) => ls.classId),
    );
    
    const filtered = allSchoolClasses.filter((c) => classIdsInSemester.has(c.classId));
    
    console.log('[CourseCascader] Filtered classes:', {
      tempSemester,
      totalClasses: allSchoolClasses.length,
      filteredCount: filtered.length,
      classes: filtered.map(c => ({ name: c.name, classId: c.classId })),
    });
    return filtered;
  }, [tempSemester, allSchoolClasses]);

  const filteredLessons = useMemo(() => {
    if (!tempCourse) return [];
    const course = courses.find((c) => c.courseId === tempCourse);
    return course?.lessons ? [...course.lessons].sort((a, b) => a.priority - b.priority) : [];
  }, [tempCourse, courses]);

  const filteredAssignments = useMemo(() => {
    if (!tempLesson) return [];
    const lesson = filteredLessons.find((l) => l.lessonId === tempLesson);
    return lesson?.assignments ? [...lesson.assignments].sort((a, b) => a.priority - b.priority) : [];
  }, [tempLesson, filteredLessons]);

  // Get display text
  const displayText = useMemo(() => {
    console.log('========================================');
    console.log('[CourseCascader] ⚡ Computing displayText:', {
      isGroupAssignment,
      urlCourseId,
      urlLessonId,
      urlAssignmentId,
      currentSemester: currentSemester?.name,
      currentSemesterId,
      currentClass: currentClass?.name,
      currentClassId,
      coursesCount: courses.length,
      coursesAvailable: courses.map(c => ({ id: c.courseId, name: c.name })),
    });

    // For group assignments, only show course/lesson/assignment
    if (isGroupAssignment) {
      if (!urlCourseId || urlCourseId === '' || !urlAssignmentId || urlAssignmentId === '') {
        return '选择课程 > 课次 > 作业';
      }
      
      if (courses.length === 0) {
        return '选择课程 > 课次 > 作业';
      }
      
      const course = courses.find((c) => c.courseId === urlCourseId);
      if (!course) {
        console.warn('[CourseCascader] Course not found in courses:', urlCourseId);
        return '选择课程 > 课次 > 作业';
      }

      let lesson = null;
      let assignment = null;

      if (urlLessonId && urlLessonId !== '') {
        lesson = course.lessons.find((l) => l.lessonId === urlLessonId);
        assignment = lesson?.assignments.find((a) => a.assignmentId === urlAssignmentId);
      } else {
        for (const l of course.lessons) {
          const foundAssignment = l.assignments.find((a) => a.assignmentId === urlAssignmentId);
          if (foundAssignment) {
            lesson = l;
            assignment = foundAssignment;
            break;
          }
        }
      }
      
      if (course && lesson && assignment) {
        const text = `${course.name} > ${lesson.name} > ${assignment.name}`;
        console.log('[CourseCascader] Group assignment display text:', text);
        return text;
      }
      console.warn('[CourseCascader] Lesson or assignment not found');
      return '选择课程 > 课次 > 作业';
    }

    // For non-group assignments, show full cascade
    if (!currentSemester || !currentClass || !urlCourseId || urlCourseId === '' || !urlAssignmentId || urlAssignmentId === '') {
      console.log('[CourseCascader] ❌ Missing required params for display:', {
        hasCurrentSemester: !!currentSemester,
        hasCurrentClass: !!currentClass,
        hasUrlCourseId: !!urlCourseId,
        hasUrlAssignmentId: !!urlAssignmentId,
      });
      return '选择学期 > 班级 > 课程 > 课次 > 作业';
    }
    
    if (courses.length === 0) {
      console.log('[CourseCascader] ❌ No courses loaded yet');
      return '选择学期 > 班级 > 课程 > 课次 > 作业';
    }
    
    console.log('[CourseCascader] 🔍 Searching for course:', {
      lookingFor: urlCourseId,
      availableCourseIds: courses.map(c => c.courseId),
    });
    
    const course = courses.find((c) => c.courseId === urlCourseId);
    if (!course) {
      console.warn('[CourseCascader] ❌ Course not found:', urlCourseId);
      return '选择学期 > 班级 > 课程 > 课次 > 作业';
    }

    console.log('[CourseCascader] ✅ Found course:', {
      courseId: course.courseId,
      courseName: course.name,
      lessonsCount: course.lessons.length,
    });

    let lesson = null;
    let assignment = null;

    if (urlLessonId && urlLessonId !== '') {
      console.log('[CourseCascader] 🔍 Searching for lesson with lessonId:', urlLessonId);
      lesson = course.lessons.find((l) => l.lessonId === urlLessonId);
      if (lesson) {
        console.log('[CourseCascader] ✅ Found lesson:', {
          lessonId: lesson.lessonId,
          lessonName: lesson.name,
          assignmentsCount: lesson.assignments.length,
        });
        assignment = lesson?.assignments.find((a) => a.assignmentId === urlAssignmentId);
      } else {
        console.warn('[CourseCascader] ❌ Lesson not found:', urlLessonId);
      }
    } else {
      console.log('[CourseCascader] 🔍 Searching for assignment without lessonId:', urlAssignmentId);
      for (const l of course.lessons) {
        const foundAssignment = l.assignments.find((a) => a.assignmentId === urlAssignmentId);
        if (foundAssignment) {
          lesson = l;
          assignment = foundAssignment;
          console.log('[CourseCascader] ✅ Found lesson by assignment:', {
            lessonId: l.lessonId,
            lessonName: l.name,
            assignmentId: foundAssignment.assignmentId,
            assignmentName: foundAssignment.name,
          });
          break;
        }
      }
    }
    
    if (course && lesson && assignment) {
      const text = `${currentSemester.name} > ${currentClass.name} > ${course.name} > ${lesson.name} > ${assignment.name}`;
      console.log('[CourseCascader] ✅ Full display text:', text);
      console.log('========================================');
      return text;
    }
    console.warn('[CourseCascader] ❌ Lesson or assignment not found in course:', {
      hasLesson: !!lesson,
      hasAssignment: !!assignment,
    });
    console.log('========================================');
    return '选择学期 > 班级 > 课程 > 课次 > 作业';
  }, [urlCourseId, urlLessonId, urlAssignmentId, courses, currentSemester, currentClass, currentSemesterId, currentClassId, isGroupAssignment]);

  // Handle temporary selections (cascade clear subsequent selections)
  const handleSemesterSelect = (semesterId: string) => {
    console.log('[CourseCascader] Semester selected:', semesterId);
    setTempSemester(semesterId);
    setTempClass(null);
    setTempCourse(null);
    setTempLesson(null);
    setTempAssignment(null);
  };

  const handleClassSelect = (classId: string) => {
    console.log('[CourseCascader] Class selected:', classId);
    setTempClass(classId);
    setTempCourse(null);
    setTempLesson(null);
    setTempAssignment(null);
  };

  const handleCourseSelect = (courseId: string) => {
    setTempCourse(courseId);
    setTempLesson(null);
    setTempAssignment(null);
  };

  const handleLessonSelect = (lessonId: string) => {
    setTempLesson(lessonId);
    setTempAssignment(null);
  };

  const handleAssignmentSelect = (assignmentId: string) => {
    console.log('🎯 [CourseCascader] ========== ASSIGNMENT SELECTED ==========');
    console.log('[CourseCascader] Assignment clicked:', assignmentId);
    
    // Auto-confirm when assignment is selected
    // Check if all required selections are made
    if (!tempCourse || !tempLesson) {
      console.warn('[CourseCascader] ❌ Missing course or lesson');
      return;
    }
    
    // For non-group assignments, ensure semester and class are also selected
    if (!isGroupAssignment && (!tempSemester || !tempClass)) {
      console.warn('[CourseCascader] ❌ Missing semester or class for non-group assignment');
      return;
    }
    
    console.log('[CourseCascader] ✅ Auto-confirming selection:', {
      tempSemester,
      tempClass,
      tempCourse,
      tempLesson,
      assignmentId,
      previousContext: {
        currentSemesterId,
        currentClassId,
      },
    });
    
    // Update context (triggers page navigation)
    if (!isGroupAssignment) {
      if (tempSemester !== currentSemesterId) {
        console.log('[CourseCascader] 🔄 Updating semester:', tempSemester);
        setCurrentSemesterId(tempSemester!);
      } else {
        console.log('[CourseCascader] ⏭️ Semester unchanged');
      }
      if (tempClass !== currentClassId) {
        console.log('[CourseCascader] 🔄 Updating class:', tempClass);
        setCurrentClassId(tempClass!);
      } else {
        console.log('[CourseCascader] ⏭️ Class unchanged');
      }
    }
    
    // Update URL parameters (all at once to avoid race conditions)
    console.log('[CourseCascader] 🔄 Updating URL params:', {
      courseId: tempCourse,
      lessonId: tempLesson,
      assignmentId,
    });
    setAllQueryParams({
      courseId: tempCourse,
      lessonId: tempLesson,
      assignmentId,
    });
    
    // Clear temp states
    console.log('[CourseCascader] 🧹 Clearing temp states');
    setTempSemester(null);
    setTempClass(null);
    setTempCourse(null);
    setTempLesson(null);
    setTempAssignment(null);
    
    // Close the dropdown
    console.log('[CourseCascader] 🚪 Closing dropdown');
    console.log('========================================');
    setIsOpen(false);
  };

  // Show loading only if we don't have any data yet
  if (loading && !data) {
    return <div className="text-[#979797] text-[18px] ml-[20px]">加载中...</div>;
  }

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-[9999]" 
          onClick={handleCancel}
        />
      )}
      
      <div className="relative ml-[20px]" ref={dropdownRef}>
        <button
          className="text-[#979797] text-[18px] px-3 py-1 border border-[#979797] rounded hover:bg-[#979797] hover:bg-opacity-10 transition-colors whitespace-nowrap"
          onClick={handleOpen}
        >
          {displayText}
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-[10000] flex flex-col">
          <div className="flex flex-row">
            {/* Semester Column - only show for non-group assignments */}
            {!isGroupAssignment && availableSemesters && availableSemesters.length > 0 && (
              <div className="w-[150px] max-h-[400px] overflow-y-auto border-r border-gray-200 flex-shrink-0">
                <div className="px-3 py-2 bg-gray-100 font-semibold text-sm sticky top-0 z-10">
                  学期
                </div>
                {availableSemesters.map((semester) => (
                  <div
                    key={semester.semesterId}
                    className={cx(
                      'px-3 py-2 cursor-pointer hover:bg-blue-50 text-sm whitespace-nowrap',
                      tempSemester === semester.semesterId && 'bg-blue-100 font-medium'
                    )}
                    onClick={() => handleSemesterSelect(semester.semesterId)}
                  >
                    {semester.name}
                  </div>
                ))}
              </div>
            )}

            {/* Class Column - only show for non-group assignments */}
            {!isGroupAssignment && (
              <div className="w-[200px] max-h-[400px] overflow-y-auto border-r border-gray-200 flex-shrink-0">
                <div className="px-3 py-2 bg-gray-100 font-semibold text-sm sticky top-0 z-10">
                  班级
                </div>
                {tempSemester ? (
                  filteredClasses.length > 0 ? (
                    filteredClasses.map((cls) => (
                      <div
                        key={cls.classId}
                        className={cx(
                          'px-3 py-2 cursor-pointer hover:bg-blue-50 text-sm whitespace-nowrap',
                          tempClass === cls.classId && 'bg-blue-100 font-medium'
                        )}
                        onClick={() => handleClassSelect(cls.classId)}
                      >
                        {cls.name}
                      </div>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-gray-400 text-sm">暂无班级</div>
                  )
                ) : (
                  <div className="px-3 py-2 text-gray-400 text-sm">请先选择学期</div>
                )}
              </div>
            )}

          {/* Course Column */}
          <div className="w-[200px] max-h-[400px] overflow-y-auto border-r border-gray-200 flex-shrink-0">
            <div className="px-3 py-2 bg-gray-100 font-semibold text-sm sticky top-0 z-10">
              课程
            </div>
            {(!isGroupAssignment && !tempClass) ? (
              <div className="px-3 py-2 text-gray-400 text-sm">请先选择班级</div>
            ) : tempLoading ? (
              <div className="px-3 py-2 text-gray-400 text-sm">加载中...</div>
            ) : courses.length > 0 ? (
              courses.map((course) => (
                <div
                  key={course.courseId}
                  className={cx(
                    'px-3 py-2 cursor-pointer hover:bg-blue-50 text-sm whitespace-nowrap',
                    tempCourse === course.courseId && 'bg-blue-100 font-medium'
                  )}
                  onClick={() => handleCourseSelect(course.courseId)}
                >
                  {course.name}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-400 text-sm">暂无课程</div>
            )}
          </div>

            {/* Lesson Column */}
            <div className="w-[200px] max-h-[400px] overflow-y-auto border-r border-gray-200 flex-shrink-0">
              <div className="px-3 py-2 bg-gray-100 font-semibold text-sm sticky top-0 z-10">
                课次
              </div>
              {tempCourse ? (
                filteredLessons.length > 0 ? (
                  filteredLessons.map((lesson) => (
                    <div
                      key={lesson.lessonId}
                      className={cx(
                        'px-3 py-2 cursor-pointer hover:bg-blue-50 text-sm whitespace-nowrap',
                        tempLesson === lesson.lessonId && 'bg-blue-100 font-medium'
                      )}
                      onClick={() => handleLessonSelect(lesson.lessonId)}
                    >
                      {lesson.name}
                    </div>
                  ))
                ) : (
                  <div className="px-3 py-2 text-gray-400 text-sm">暂无课次</div>
                )
              ) : (
                <div className="px-3 py-2 text-gray-400 text-sm">请先选择课程</div>
              )}
            </div>

            {/* Assignment Column */}
            <div className="w-[200px] max-h-[400px] overflow-y-auto flex-shrink-0">
              <div className="px-3 py-2 bg-gray-100 font-semibold text-sm sticky top-0 z-10">
                作业
              </div>
              {tempLesson ? (
                filteredAssignments.length > 0 ? (
                  filteredAssignments.map((assignment) => (
                    <div
                      key={assignment.assignmentId}
                      className={cx(
                        'px-3 py-2 cursor-pointer hover:bg-blue-50 text-sm whitespace-nowrap',
                        tempAssignment === assignment.assignmentId && 'bg-blue-100 font-medium'
                      )}
                      onClick={() => handleAssignmentSelect(assignment.assignmentId)}
                    >
                      {assignment.name}
                    </div>
                  ))
                ) : (
                  <div className="px-3 py-2 text-gray-400 text-sm">暂无作业</div>
                )
              ) : (
                <div className="px-3 py-2 text-gray-400 text-sm">请先选择课次</div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between items-center gap-2 px-3 py-2 bg-gray-50 border-t border-gray-200">
            <div className="flex-1">
              {children}
            </div>
            <div className="flex gap-2">
              <button
                className="px-4 py-1 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded transition-colors"
                onClick={handleCancel}
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

